import find from 'lodash/find';
import last from 'lodash/last';
import uniq from 'lodash/uniq';
import assign from 'lodash/assign';

const catalogTree = {
  catalog: {
    title: null,
    url: '/catalog',
  },
};

const genreTree = {
  catalog: {
    title: 'All',
    url: '/catalog',
    value: 'catalog',
  },
  rock: {
    title: 'Rock',
    url: '/rock',
  },
  country: {
    title: 'Country',
    url: '/country',
  },
  pop: {
    title: 'Pop',
    url: '/pop',
  },
  blues: {
    title: 'Blues',
    url: '/blues',
  },
  folk: {
    title: 'Folk',
    url: '/folk',
  },
  rnbsoul: {
    title: 'R&B/Soul',
    url: '/rnbsoul',
  },
};

const bassGenreTree = {
  catalog: {
    title: 'All',
    url: '/catalog',
    value: 'catalog',
  },
  rock: {
    title: 'Rock',
    url: '/rock',
  },
  funk: {
    title: 'Funk',
    url: '/funk',
  },
};

const skillsTree = {
  basics: {
    title: 'Basics',
    url: '/basics',
  },
  courses: {
    title: 'Courses',
    url: '/courses',
  },
  exercises: {
    title: 'Exercises',
    url: '/exercises',
  },
  glossary: {
    title: 'Glossary',
    url: '/glossary',
  },
  techniques: {
    title: 'Techniques',
    url: '/techniques',
  },
  theory: {
    title: 'Theory',
    url: '/theory',
  },
};

function flatMapFilter(tree, selectedKeyArr = null) {
  const flattened = [];
  const parseTreeItem = function parseItemData(item, prependUrl = '') {
    const { current, items, label, title, url } = item;
    let parsedCurrent = current;
    if (selectedKeyArr) {
      selectedKeyArr.forEach(selectedKey => {
        const foundKey = find(items, (i, key) => key === selectedKey);
        if (foundKey) {
          parsedCurrent = selectedKey;
        }
      });
    }
    const itemNavLinkArr = [];
    if (items) {
      Object.keys(items).forEach(itemNavKey => {
        const itemNavObj = items[itemNavKey];
        const itemUrl = url || '';
        const itemCustomUrl = itemNavObj.customUrl || null;
        if (itemNavObj.title && itemNavObj.url) {
          const parsedURL = itemCustomUrl || `${prependUrl}${itemUrl}${itemNavObj.url}`;
          itemNavLinkArr.push({
            id: itemNavKey,
            label: itemNavObj.title,
            title: itemNavObj.title,
            url: parsedURL,
            value: parsedURL,
          });
        }
        if (itemNavKey === parsedCurrent && itemNavObj.items) {
          parseItemData(itemNavObj, `${prependUrl}${itemUrl}`);
        }
      });
    }
    const parsedObj = {
      current: parsedCurrent,
      label,
      title,
      url,
      items: itemNavLinkArr,
      value: url,
    };
    if (itemNavLinkArr.length) {
      flattened.push(parsedObj);
    }
  };
  parseTreeItem(tree);
  return flattened.reverse();
}

function getFilterTree(
  settings = {},
  instruments = ['guitar', 'ukulele'],
  prefix = '',
  suffix = '',
) {
  const { category, courseType, genre, instrument, skill, type, level } = settings;

  const filterTree = {
    current: instrument || 'guitar',
    label: 'Instrument',
    items: {},
  };

  const bassFilters = {
    bass: {
      current: courseType || 'songRiff',
      customUrl: `${prefix}/bass${suffix}/${
        courseType === 'skill' ? 'skills' : 'songs'
      }/all/catalog`,
      title: 'Bass',
      value: 'bass',
      url: `${prefix}/bass${suffix}`,
      items: {
        songRiff: {
          current: type || 'songs',
          label: 'Type',
          items: {
            songs: {
              title: 'Songs',
              url: '/songs',
              customUrl: `${prefix}/bass${suffix}/songs/all/catalog`,
              label: 'Difficulty Level',
              current: level || 'all',
              items: {
                all: {
                  title: 'All',
                  url: '/all',
                  customUrl: `${prefix}/bass${suffix}/songs/all/catalog`,
                  label: 'Genres',
                  current: genre || 'catalog',
                  items: { ...bassGenreTree },
                },
                'made-easy': {
                  title: 'Made Easy',
                  url: '/made-easy',
                  customUrl: `${prefix}/bass${suffix}/songs/made-easy/catalog`,
                  label: 'Genres',
                  current: genre || 'catalog',
                  items: { ...bassGenreTree },
                },
                'making-progress': {
                  title: 'Making Progress',
                  url: '/making-progress',
                  customUrl: `${prefix}/bass${suffix}/songs/making-progress/catalog`,
                  label: 'Genres',
                  current: genre || 'catalog',
                  items: { ...bassGenreTree },
                },
                challenging: {
                  title: 'Challenging',
                  url: '/challenging',
                  customUrl: `${prefix}/bass${suffix}/songs/challenging/catalog`,
                  label: 'Genres',
                  current: genre || 'catalog',
                  items: { ...bassGenreTree },
                },
              },
            },
            riffs: {
              title: 'Riffs',
              url: '/riffs',
              customUrl: `${prefix}/bass${suffix}/riffs/all/catalog`,
              label: 'Difficulty Level',
              current: level || 'all',
              items: {
                all: {
                  title: 'All',
                  url: '/all',
                  customUrl: `${prefix}/bass${suffix}/riffs/all/catalog`,
                  label: 'Genres',
                  current: genre || 'catalog',
                  items: { ...bassGenreTree },
                },
                'made-easy': {
                  title: 'Made Easy',
                  url: '/made-easy',
                  customUrl: `${prefix}/bass${suffix}/riffs/made-easy/catalog`,
                  label: 'Genres',
                  current: genre || 'catalog',
                  items: { ...bassGenreTree },
                },
                'making-progress': {
                  title: 'Making Progress',
                  url: '/making-progress',
                  customUrl: `${prefix}/bass${suffix}/riffs/making-progress/catalog`,
                  label: 'Genres',
                  current: genre || 'catalog',
                  items: { ...bassGenreTree },
                },
                challenging: {
                  title: 'Challenging',
                  url: '/challenging',
                  customUrl: `${prefix}/bass${suffix}/riffs/challenging/catalog`,
                  label: 'Genres',
                  current: genre || 'catalog',
                  items: { ...bassGenreTree },
                },
              },
            },
          },
        },
        skill: {
          current: category || 'skills',
          label: 'Category',
          items: {
            skills: {
              current: skill || 'basics',
              customUrl: `${prefix}/bass${suffix}/skills/basics`,
              label: 'Category',
              title: null,
              url: '/skills',
              items: { ...skillsTree },
            },
          },
        },
      },
    },
  };

  const guitarFilters = {
    guitar: {
      title: 'Guitar',
      url: `${prefix}/guitar${suffix}`,
      value: 'guitar',
      current: courseType || 'songRiff',
      customUrl: `${prefix}/guitar${suffix}/${
        courseType === 'skill' ? 'skills' : 'songs'
      }/all/catalog`,
      items: {
        songRiff: {
          label: 'Type',
          current: type || 'songs',
          items: {
            songs: {
              title: 'Songs',
              url: '/songs',
              customUrl: `${prefix}/guitar${suffix}/songs/all/catalog`,
              label: 'Difficulty Level',
              current: level || 'all',
              items: {
                all: {
                  title: 'All',
                  url: '/all',
                  customUrl: `${prefix}/guitar${suffix}/songs/all/catalog`,
                  label: 'Genres',
                  current: genre || 'catalog',
                  items: { ...genreTree },
                },
                'made-easy': {
                  title: 'Made Easy',
                  url: '/made-easy',
                  customUrl: `${prefix}/guitar${suffix}/songs/made-easy/catalog`,
                  label: 'Genres',
                  current: genre || 'catalog',
                  items: { ...genreTree },
                },
                'making-progress': {
                  title: 'Making Progress',
                  url: '/making-progress',
                  customUrl: `${prefix}/guitar${suffix}/songs/making-progress/catalog`,
                  label: 'Genres',
                  current: genre || 'catalog',
                  items: { ...genreTree },
                },
                challenging: {
                  title: 'Challenging',
                  url: '/challenging',
                  customUrl: `${prefix}/guitar${suffix}/songs/challenging/catalog`,
                  label: 'Genres',
                  current: genre || 'catalog',
                  items: { ...genreTree },
                },
              },
            },
            riffs: {
              title: 'Riffs',
              url: '/riffs',
              customUrl: `${prefix}/guitar${suffix}/riffs/all/catalog`,
              label: 'Difficulty Level',
              current: level || 'all',
              items: {
                all: {
                  title: 'All',
                  url: '/all',
                  customUrl: `${prefix}/guitar${suffix}/riffs/all/catalog`,
                  label: 'Genres',
                  current: genre || 'catalog',
                  items: { ...genreTree },
                },
                'made-easy': {
                  title: 'Made Easy',
                  url: '/made-easy',
                  customUrl: `${prefix}/guitar${suffix}/riffs/made-easy/catalog`,
                  label: 'Genres',
                  current: genre || 'catalog',
                  items: { ...genreTree },
                },
                'making-progress': {
                  title: 'Making Progress',
                  url: '/making-progress',
                  customUrl: `${prefix}/guitar${suffix}/riffs/making-progress/catalog`,
                  label: 'Genres',
                  current: genre || 'catalog',
                  items: { ...genreTree },
                },
                challenging: {
                  title: 'Challenging',
                  url: '/challenging',
                  customUrl: `${prefix}/guitar${suffix}/riffs/challenging/catalog`,
                  label: 'Genres',
                  current: genre || 'catalog',
                  items: { ...genreTree },
                },
              },
            },
          },
        },
        skill: {
          current: category || 'skills',
          label: 'Category',
          items: {
            skills: {
              current: skill || 'basics',
              customUrl: `${prefix}/guitar${suffix}/skills/basics`,
              label: 'Category',
              title: null,
              url: '/skills',
              items: {
                chords: {
                  title: 'Chords',
                  url: '/chords',
                },
                ...skillsTree,
                tone: {
                  title: 'Tone',
                  url: '/tone',
                },
              },
            },
          },
        },
      },
    },
  };

  const ukuleleFilters = {
    ukulele: {
      current: courseType || 'songRiff',
      customUrl: `${prefix}/ukulele${suffix}/${
        courseType === 'skill' ? 'skills' : 'songs'
      }/all/catalog`,
      title: 'Ukulele',
      url: `${prefix}/ukulele${suffix}`,
      items: {
        songRiff: {
          current: type || 'songs',
          customUrl: `${prefix}/ukulele${suffix}/songs`,
          label: 'Type',
          items: {
            songs: {
              url: '/songs',
              customUrl: `${prefix}/ukulele${suffix}/songs/all/catalog`,
              title: 'Songs',
              label: 'Difficulty Level',
              current: level || 'all',
              items: {
                all: {
                  title: 'All',
                  url: '/all',
                  customUrl: `${prefix}/ukulele${suffix}/songs/all/catalog`,
                  label: 'Genres',
                  current: 'catalog',
                  items: { ...catalogTree },
                },
                'made-easy': {
                  title: 'Made Easy',
                  url: '/made-easy',
                  customUrl: `${prefix}/ukulele${suffix}/songs/made-easy/catalog`,
                  label: 'Genres',
                  current: 'catalog',
                  items: { ...catalogTree },
                },
                'making-progress': {
                  title: 'Making Progress',
                  url: '/making-progress',
                  customUrl: `${prefix}/ukulele${suffix}/songs/making-progress/catalog`,
                  label: 'Genres',
                  current: 'catalog',
                  items: { ...catalogTree },
                },
                challenging: {
                  title: 'Challenging',
                  url: '/challenging',
                  customUrl: `${prefix}/ukulele${suffix}/songs/challenging/catalog`,
                  label: 'Genres',
                  current: 'catalog',
                  items: { ...catalogTree },
                },
              },
            },
            riffs: {
              title: 'Riffs',
              url: '/riffs',
              customUrl: `${prefix}/ukulele${suffix}/riffs/all/catalog`,
              label: 'Difficulty Level',
              current: level || 'all',
              items: {
                all: {
                  title: 'All',
                  url: '/all',
                  customUrl: `${prefix}/ukulele${suffix}/riffs/all/catalog`,
                  label: 'Genres',
                  current: 'catalog',
                  items: { ...catalogTree },
                },
                'made-easy': {
                  title: 'Made Easy',
                  url: '/made-easy',
                  customUrl: `${prefix}/ukulele${suffix}/riffs/made-easy/catalog`,
                  label: 'Genres',
                  current: 'catalog',
                  items: { ...catalogTree },
                },
                'making-progress': {
                  title: 'Making Progress',
                  url: '/making-progress',
                  customUrl: `${prefix}/ukulele${suffix}/riffs/making-progress/catalog`,
                  label: 'Genres',
                  current: 'catalog',
                  items: { ...catalogTree },
                },
                challenging: {
                  title: 'Challenging',
                  url: '/challenging',
                  customUrl: `${prefix}/ukulele${suffix}/riffs/challenging/catalog`,
                  label: 'Genres',
                  current: 'catalog',
                  items: { ...catalogTree },
                },
              },
            },
          },
        },
        skill: {
          current: category || 'skills',
          label: 'Category',
          items: {
            skills: {
              current: skill || 'basics',
              customUrl: `${prefix}/ukulele${suffix}/skills/basics`,
              label: 'Category',
              title: null,
              url: '/skills',
              items: {
                chords: {
                  title: 'Chords',
                  url: '/chords',
                },
                ...skillsTree,
              },
            },
          },
        },
      },
    },
  };

  instruments.forEach(inst => {
    if (inst === 'bass') {
      assign(filterTree.items, bassFilters);
    } else if (inst === 'guitar') {
      assign(filterTree.items, guitarFilters);
    } else if (inst === 'ukulele') {
      assign(filterTree.items, ukuleleFilters);
    }
  });

  return filterTree;
}

function getFilterNameArray(filterArr = []) {
  const filtered = filterArr.map(item => {
    const current = item.current === 'start-at' ? 'recent' : item.current;
    const { items } = item;
    const currentItem = find(items, { id: current });
    if (currentItem) {
      return currentItem.label || currentItem.title;
    }
    return null;
  });
  return uniq(filtered).filter(item => item);
}

function getLastCurrentUrl(filterArr) {
  if (!filterArr) {
    return '';
  }
  const lastArr = last(filterArr);
  if (lastArr) {
    const { current } = lastArr;
    const currentItem = find(lastArr.items, { id: current }) || lastArr.items[0];
    return currentItem.url;
  }
  return '';
}

function getSortTree(url, current) {
  return {
    current: current || 'recent',
    items: {
      recent: {
        title: 'Recent',
        url: `${url}?sort=start-at`,
      },
      asc: {
        title: 'A-Z',
        url: `${url}?sort=asc`,
      },
      desc: {
        title: 'Z-A',
        url: `${url}?sort=desc`,
      },
    },
    label: 'Sort',
  };
}

const getLevelFilterText = (title = '') => {
  switch (title) {
    case '0':
      return 'All';
    case '1':
      return 'Made Easy';
    case '2':
      return 'Making Progress';
    case '3':
      return 'Challenging';
    default:
      return title;
  }
};

const getLevelFilterNumber = title => {
  switch (title) {
    case 'All':
      return 0;
    case 'Made Easy':
      return 1;
    case 'Making Progress':
      return 2;
    case 'Challenging':
      return 3;
    default:
      return 0;
  }
};

export {
  flatMapFilter,
  genreTree,
  getFilterNameArray,
  getFilterTree,
  getLastCurrentUrl,
  getSortTree,
  skillsTree,
  getLevelFilterText,
  getLevelFilterNumber,
};
